import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import CalendarAppResource from './CalendarAppResource';
import CalendarEventAppResource from './CalendarEventAppResource';
import ChannelAppResource from './ChannelAppResource';
import FileAppResource from './FileAppResource';
import OrganizationAppResource from './OrganizationAppResource';
import OrganizationMemberAppResource from './OrganizationMemberAppResource';
import PropertyInstanceAppResource from './PropertyInstanceAppResource';
import ServiceTicketAppResource from './ServiceTicketAppResource';
import StageAppResource from './StageAppResource';
import StageRoleAppResource from './StageRoleAppResource';
import TaskAppResource from './TaskAppResource';
import TaskCategoryAppResource from './TaskCategoryAppResource';
import TaskPriorityAppResource from './TaskPriorityAppResource';
import TaskRoleAppResource from './TaskRoleAppResource';
import TaskStatusAppResource from './TaskStatusAppResource';
import UserAppResource from './UserAppResource';
import UserInvitationAppResource from './UserInvitationAppResource';
import WorkspaceOrganizationMemberAppResource from './WorkspaceOrganizationMemberAppResource';
import WorkspaceTypeAppResource from './WorkspaceTypeAppResource';
import MarketAppResource from "./MarketAppResource";
import PlaybookAppResource from "./PlaybookAppResource";
import ProjectBillboardAppResource from "./ProjectBillboardAppResource";
import BillboardAppResource from "./BillboardAppResource";
import MessageAppResource from "./MessageAppResource";
import MlsMarketAppResource from './MlsMarketAppResource';
import CityAppResource from './CityAppResource';
import PropertyAppResource from "./PropertyAppResource";
import ProcessAppResource from "./ProcessAppResource";
import MLSAppResource from "./MLSAppResource";
import LuWorkspacePipelineStatusAppResource from "./LuWorkspacePipelineStatusAppResource";
import ProcessInstanceAppResource from "./ProcessInstanceAppResource";
import PropertySearchFilterAppResource from "./PropertySearchFilterAppResource";
import TransactionWorkspaceAppResource from "./TransactionWorkspaceAppResource";
import MoveHubAppResource from "./MoveHubAppResource";
import Engine from "@milestoneslabs/milestones-js-engine/src/Engine";
import isEmpty from "lodash/isEmpty";
import forEach from "lodash/forEach";
import LuCommunicationContextAppResource from "./LuCommunicationContextAppResource";

export const ASAP = 'asap';
export const NEXT_6_MONTHS = 'next-6-months';
export const NEXT_6_TO_12_MONTHS = 'next-6-to-12-months';
export const NOT_SURE = 'not-sure';
export const NO = 'no';

export const PERIODS = [
    ASAP,
    NEXT_6_MONTHS,
    NEXT_6_TO_12_MONTHS,
    NOT_SURE,
    NO,
];

class WorkspaceAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'Workspace';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'workspaces';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'name',
            'description',
            'workspaceTypeUuid',
            'ownerUserUuid',
            'organizationUuid',
            'ownerOrganizationMemberUuid',
            'marketUuid',
            'status',
            'workspaceType',
            'ownerUser',
            'organization',
            'ownerOrganizationMember',
            'users',
            'files',
            'propertyInstances',
            'stages',
            'stageRoles',
            'channels',
            'members',
            'tasks',
            'taskCategories',
            'taskPriorities',
            'taskStatuses',
            'taskRoles',
            'calendars',
            'calendarEvents',
            'userInvitations',
            'market',
            'playbookUuid',
            'playbook',
            'settings',
            'ownerProjectMemberRoleUuid',
            'projectBillboards',
            'billboards',
            'unreadMessages',
            'mlsMarketUuid',
            'mlsMarket',
            'city',
            'cityUuid',
            'properties',
            'processes',
            'processInstances',
            'clientGatewayUuid',
            'landingPageUuid',
            'mlsDefinitionUuid',
            'mls',
            'pipelineStatusUuid',
            'pipelineStatus',
            'meta',
            'period',
            'archivedAt',
            'attomId',
            'plunkParcelId',
            'lookupCommunicationContextUuid',
            'source',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            workspaceType: WorkspaceTypeAppResource,
            ownerUser: UserAppResource,
            organization: OrganizationAppResource,
            ownerOrganizationMember: OrganizationMemberAppResource,
            users: UserAppResource,
            files: FileAppResource,
            propertyInstances: PropertyInstanceAppResource,
            stages: StageAppResource,
            stageRoles: StageRoleAppResource,
            channels: ChannelAppResource,
            members: WorkspaceOrganizationMemberAppResource,
            tasks: TaskAppResource,
            taskCategories: TaskCategoryAppResource,
            taskPriorities: TaskPriorityAppResource,
            taskStatuses: TaskStatusAppResource,
            taskRoles: TaskRoleAppResource,
            calendars: CalendarAppResource,
            calendarEvents: CalendarEventAppResource,
            serviceTickets: ServiceTicketAppResource,
            userInvitations: UserInvitationAppResource,
            market: MarketAppResource,
            playbook: PlaybookAppResource,
            projectBillboards: ProjectBillboardAppResource,
            billboards: BillboardAppResource,
            unreadMessages: MessageAppResource,
            mlsMarket: MlsMarketAppResource,
            city: CityAppResource,
            properties: PropertyAppResource,
            processes: ProcessAppResource,
            processInstances: ProcessInstanceAppResource,
            mls: MLSAppResource,
            pipelineStatus: LuWorkspacePipelineStatusAppResource,
            savedSearches: PropertySearchFilterAppResource,
            transaction: TransactionWorkspaceAppResource,
            moveHubs: MoveHubAppResource,
            homeOwners: WorkspaceOrganizationMemberAppResource,
            homeBuyers: WorkspaceOrganizationMemberAppResource,
            lookupCommunicationContext: LuCommunicationContextAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            workspaceType: 'workspace_type_uuid',
            ownerUser: 'owner_user_uuid',
            organization: 'organization_uuid',
            ownerOrganizationMember: 'owner_organization_member_uuid',
            market: 'market_uuid',
            playbook: 'playbook_uuid',
            projectBillboards: 'project_uuid',
            billboards: 'project_uuid',
            landingPage: 'landing_page_uuid',
            mls: 'mls_definition_uuid',
            pipelineStatus: 'pipeline_status_uuid',
            lookupCommunicationContext: 'lookup_communication_context_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            name: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            description: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                },
            },
            workspaceTypeUuid: {
                type: 'string',
                presence: true,
            },
            ownerOrganizationMemberUuid: {
                type: 'string',
            },
            ownerProjectMemberRoleUuid: {
                type: 'string',
            },
            marketUuid: {
                type: 'string',
                presence: false,
            },
            playbookUuid: {
                type: 'string',
            },
            status: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            clientGatewayUuid: {
                type: 'string',
            },
            landingPageUuid: {
                type: 'string',
            },
            mlsDefinitionUuid: {
                type: 'string',
            },
            pipelineStatusUuid: {
                type: 'string',
            },
            meta: {},
            period: {
                type: 'string',
            },
            lookupCommunicationContextUuid: {
                type: 'string',
            },
        };
    }

    apiPaths(environment = null) {
        return {
            ...super.apiPaths(environment),
            logActivity: Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/activities/logs'),
            createDefaultHoh: Engine.apiUrl(this.appResourceApiPrefix() + '/default-workspace-create'),
            getMyHomeDataCards: Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/data-cards'),
        };
    }

    async logActivity(activityType) {
        if (!this.uuid) {
            return Promise.resolve(null);
        }

        const apiResponse = await this.enginePost(this.apiPaths().logActivity.replace('{uuid}', this.uuid), {
            type: activityType
        });

        return apiResponse;
    }

    async createDefaultHoh(data, config = {}) {
        const apiResponse = await this.enginePost(this.apiPaths().createDefaultHoh, data, config);

        if (isEmpty(apiResponse.resources)) {
            return Promise.resolve(null)
        }
        forEach(apiResponse.resources.appResources, (appResource, uuid) => {
            this.syncFromRemoteAppResource(appResource)
        })

        return apiResponse;
    }

    async getMyHomeDataCards() {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        const url = this.apiPaths().getMyHomeDataCards.replace('{uuid}', this.uuid);
        const apiResponse = await Engine.get(url);

        return apiResponse?.rawData;
    }
}

export default WorkspaceAppResource;
